import { isValidIban, parseMoneyToCents, tt, type MoneyInput } from '@agos/shared'

/**
 * A standing charge, and when it will next bill you.
 *
 * The backend decides what is due with one query:
 *
 * ```sql
 * WHERE active = true AND day_of_month <= :day
 *   AND (last_generated_at IS NULL
 *        OR to_char(last_generated_at …, 'YYYY-MM') < :yearMonth)
 * ```
 *
 * — active, its day has arrived this month, and it has not already been generated this
 * calendar month. `next_due_date` is a **stored column the generator writes**, so a
 * template that has never run has none.
 *
 * Three of the four templates in the restored data are in exactly that state: *Nájom
 * prevádzky* (5th), *Internet + Telefón* (15th) and *Elektrina* (20th) are all active
 * with `nextDueDate: null` and `lastGeneratedAt: null`. Only *Nájom priestorov* has ever
 * generated. So the page's *Ďalšia splatnosť* column is empty for three live standing
 * charges — and **all three are due the moment somebody presses Spustiť**, which nothing
 * says either.
 *
 * That is FB-085, and the rule is the same one FB-060 and FB-081 set: the figure is
 * computable from data the page already has, so it is computed and labelled as derived.
 */

export interface RecurringTemplate {
  id?: number
  supplierId?: number
  supplierName?: string | null
  name?: string
  /** The gross amount billed each period. */
  amountGross?: MoneyInput
  /** A percentage — a multiplicand, so it keeps its decimals (FB-056). */
  vatRate?: number
  currency?: string
  dayOfMonth?: number
  dueDays?: number
  constantSymbol?: string | null
  iban?: string | null
  description?: string | null
  active?: boolean
  /** Written by the generator. `null` until the template has run once. */
  nextDueDate?: string | null
  lastGeneratedAt?: string | null
}

/** Postgres prints `2026-07-29 02:12:58.075934+00`; `Date.parse` refuses it twice. */
export function parseStamp(value: string | null | undefined): number | null {
  const text = (value ?? '').trim()
  if (text === '') return null
  const parsed = Date.parse(text.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'))
  return Number.isFinite(parsed) ? parsed : null
}

/** `YYYY-MM` in Bratislava, the key the backend compares on. */
export function yearMonthOf(instant: number): string {
  const date = new Date(instant)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

/**
 * Whether this template would be generated by a run right now.
 *
 * Mirrors `dueTemplateIds` exactly, condition for condition — the point of the column is
 * to tell somebody what pressing the button will do.
 */
export function isDueNow(template: RecurringTemplate, now: number): boolean {
  if (template.active === false) return false

  const day = template.dayOfMonth ?? 0
  const today = new Date(now)
  if (day > today.getDate()) return false

  const generated = parseStamp(template.lastGeneratedAt)
  if (generated === null) return true
  return yearMonthOf(generated) < yearMonthOf(now)
}

export interface NextOccurrence {
  /** The date the invoice would be issued, `YYYY-MM-DD`. */
  issueDate: string
  /** `issueDate + dueDays` — when it would have to be paid. */
  dueDate: string
  /** Whether the server told us, or this was worked out here. */
  derived: boolean
  /** True when a run right now would generate it. */
  dueNow: boolean
}

/**
 * When this template next bills, from the server if it knows and from the rules if not.
 *
 * The server's `nextDueDate` wins whenever it has one. Otherwise: this month if its day
 * has not passed *and* it has not already run this month, next month otherwise.
 */
export function nextOccurrence(
  template: RecurringTemplate,
  now: number,
): NextOccurrence | null {
  const day = template.dayOfMonth ?? 0
  if (day < 1) return null

  const dueNow = isDueNow(template, now)
  const stored = (template.nextDueDate ?? '').trim()
  if (stored !== '') {
    return {
      issueDate: stored,
      dueDate: addDays(stored, template.dueDays ?? 0),
      derived: false,
      dueNow,
    }
  }

  const today = new Date(now)
  const generatedThisMonth =
    parseStamp(template.lastGeneratedAt) !== null &&
    yearMonthOf(parseStamp(template.lastGeneratedAt)!) === yearMonthOf(now)

  const thisMonth = day >= today.getDate() && !generatedThisMonth
  const target = new Date(today.getFullYear(), today.getMonth() + (thisMonth ? 0 : 1), 1)
  const issueDate = `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`

  return { issueDate, dueDate: addDays(issueDate, template.dueDays ?? 0), derived: true, dueNow }
}

/** `2026-08-01` plus 14 days. Date arithmetic, not money — no rounding involved. */
export function addDays(date: string, days: number): string {
  const parsed = new Date(`${date}T12:00:00`)
  if (Number.isNaN(parsed.getTime())) return date
  parsed.setDate(parsed.getDate() + days)
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`
}

export interface TemplateTotals {
  count: number
  activeCount: number
  /** What the active templates bill in a month, in cents. */
  monthlyCents: number
  /** How many a run right now would generate. */
  dueNowCount: number
  /** How many have never generated, so nothing has ever come of them. */
  neverRunCount: number
}

export function templateTotals(templates: RecurringTemplate[], now: number): TemplateTotals {
  const active = templates.filter((template) => template.active !== false)
  return {
    count: templates.length,
    activeCount: active.length,
    monthlyCents: active.reduce(
      (sum, template) => sum + parseMoneyToCents(template.amountGross),
      0,
    ),
    dueNowCount: templates.filter((template) => isDueNow(template, now)).length,
    neverRunCount: templates.filter(
      (template) => parseStamp(template.lastGeneratedAt) === null,
    ).length,
  }
}

export interface TemplateForm {
  supplierId: string
  name: string
  amountGross: string
  vatRate: string
  currency: string
  dayOfMonth: string
  dueDays: string
  constantSymbol: string
  iban: string
  description: string
  active: boolean
}

/** Legacy's defaults: 23 % — the current Slovak standard rate — the 1st, and 14 days. */
export const EMPTY_TEMPLATE: TemplateForm = {
  supplierId: '',
  name: '',
  amountGross: '',
  vatRate: '23',
  currency: 'EUR',
  dayOfMonth: '1',
  dueDays: '14',
  constantSymbol: '0308',
  iban: '',
  description: '',
  active: true,
}

export function formOf(template: RecurringTemplate | null): TemplateForm {
  if (!template) return { ...EMPTY_TEMPLATE }
  return {
    supplierId: template.supplierId === undefined ? '' : String(template.supplierId),
    name: template.name ?? '',
    amountGross: template.amountGross === undefined ? '' : String(template.amountGross),
    vatRate: template.vatRate === undefined ? '' : String(template.vatRate),
    currency: template.currency ?? 'EUR',
    dayOfMonth: String(template.dayOfMonth ?? 1),
    dueDays: String(template.dueDays ?? 14),
    constantSymbol: template.constantSymbol ?? '',
    iban: template.iban ?? '',
    description: template.description ?? '',
    active: template.active !== false,
  }
}

/** Legacy's own cap, and the API's: no 29th, 30th or 31st, because not every month has one. */
export const MAX_DAY_OF_MONTH = 28

/** The API's caps, which legacy's form does not state. */
export const MAX_DUE_DAYS = 60
export const MAX_VAT_RATE = 50

export type TemplateProblem =
  | { kind: 'no-supplier' }
  | { kind: 'no-name' }
  | { kind: 'no-amount' }
  | { kind: 'day-out-of-range'; value: number }
  | { kind: 'due-days-negative'; value: number }
  | { kind: 'due-days-too-long'; value: number }
  | { kind: 'vat-rate-too-high'; value: number }
  | { kind: 'iban-checksum'; value: string }

export function templateProblems(form: TemplateForm): TemplateProblem[] {
  const problems: TemplateProblem[] = []
  if (form.supplierId.trim() === '') problems.push({ kind: 'no-supplier' })
  if (form.name.trim() === '') problems.push({ kind: 'no-name' })
  if (parseMoneyToCents(form.amountGross) <= 0) problems.push({ kind: 'no-amount' })

  const day = Number(form.dayOfMonth.trim())
  if (!Number.isInteger(day) || day < 1 || day > MAX_DAY_OF_MONTH) {
    problems.push({ kind: 'day-out-of-range', value: day })
  }

  const dueDays = Number(form.dueDays.trim())
  if (Number.isFinite(dueDays) && dueDays < 0) {
    problems.push({ kind: 'due-days-negative', value: dueDays })
  } else if (Number.isFinite(dueDays) && dueDays > MAX_DUE_DAYS) {
    // The API caps it; legacy's form does not, so a 90-day term is rejected on save
    // with a validation error rather than refused as it is typed.
    problems.push({ kind: 'due-days-too-long', value: dueDays })
  }

  const vatRate = Number(form.vatRate.trim())
  if (Number.isFinite(vatRate) && vatRate > MAX_VAT_RATE) {
    problems.push({ kind: 'vat-rate-too-high', value: vatRate })
  }

  const iban = form.iban.trim()
  if (iban !== '' && !isValidIban(iban)) {
    problems.push({ kind: 'iban-checksum', value: iban })
  }

  return problems
}

export function canSave(problems: TemplateProblem[]): boolean {
  return problems.length === 0
}

export interface TemplatePayload {
  supplierId: number
  name: string
  amountGross: number
  vatRate: number
  currency: string
  dayOfMonth: number
  dueDays: number
  constantSymbol?: string
  iban?: string
  description?: string
  active: boolean
}

/**
 * The body `POST`/`PATCH /recurring-invoices` wants.
 *
 * The two verbs are described differently, and it matters. **POST** takes the typed
 * `RecurringInvoiceTemplateRequest`, whose four optional strings are `{"type": "string"}`
 * with no `nullable` — so a blank one is **omitted**, which on a creation is the same
 * thing. **PATCH** takes an untyped map, which is the right shape for a partial update
 * and means `null` *is* sendable there — so `patchPayload` sends it, and a constant
 * symbol can actually be cleared.
 */
export function templatePayload(form: TemplateForm): TemplatePayload {
  const optional = (value: string) => {
    const trimmed = value.trim()
    return trimmed === '' ? undefined : trimmed
  }

  return {
    supplierId: Number(form.supplierId),
    name: form.name.trim(),
    // Gross money, so cents — this is an amount somebody pays (§14.7).
    amountGross: parseMoneyToCents(form.amountGross) / 100,
    // A rate, so every decimal it was given (FB-056).
    vatRate: Number(form.vatRate.trim()) || 0,
    currency: form.currency.trim() || 'EUR',
    dayOfMonth: Number(form.dayOfMonth.trim()) || 1,
    dueDays: Number(form.dueDays.trim()) || 0,
    constantSymbol: optional(form.constantSymbol),
    // Unspaced, so two templates for one account are one string — the data holds both
    // `SK3112000000198742637541` and `SK50 1100 0000 0029 2283 3577` today.
    iban: optional(form.iban.replace(/\s/g, '').toUpperCase()),
    description: optional(form.description),
    active: form.active,
  }
}

/**
 * The same body for a PATCH, with cleared fields explicitly nulled.
 *
 * A partial update that omitted them would leave the stored value alone — the FB-051
 * trap — and unlike the POST, this endpoint's body is an untyped map, so `null` is
 * sendable.
 */
export function patchPayload(form: TemplateForm): Record<string, unknown> {
  const payload = templatePayload(form)
  return {
    ...payload,
    constantSymbol: payload.constantSymbol ?? null,
    iban: payload.iban ?? null,
    description: payload.description ?? null,
  }
}

/** Due-now first, then by day of month, then by name. */
export function byUrgency(templates: RecurringTemplate[], now: number): RecurringTemplate[] {
  return [...templates].sort((left, right) => {
    const leftDue = isDueNow(left, now) ? 0 : 1
    const rightDue = isDueNow(right, now) ? 0 : 1
    if (leftDue !== rightDue) return leftDue - rightDue

    const leftActive = left.active === false ? 1 : 0
    const rightActive = right.active === false ? 1 : 0
    if (leftActive !== rightActive) return leftActive - rightActive

    const byDay = (left.dayOfMonth ?? 0) - (right.dayOfMonth ?? 0)
    if (byDay !== 0) return byDay
    return (left.name ?? '').localeCompare(right.name ?? '', 'sk')
  })
}

export function matchesSearch(template: RecurringTemplate, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return [template.name, template.supplierName, template.description, template.iban].some((field) =>
    String(field ?? '').toLowerCase().includes(needle),
  )
}

export interface RunOutcome {
  generated: number
  scanned: number
}

export function readRun(result: unknown): RunOutcome {
  const record = typeof result === 'object' && result !== null ? (result as Record<string, unknown>) : {}
  const number = (value: unknown) => (typeof value === 'number' ? value : 0)
  return { generated: number(record.generated), scanned: number(record.scanned) }
}

/** A run that generated fewer than it scanned means some template failed. */
export function runMessage(outcome: RunOutcome): string {
  if (outcome.scanned === 0) return tt('Žiadna šablóna nebola na vygenerovanie.')
  if (outcome.generated === outcome.scanned) return tt('Všetky splatné šablóny sa vygenerovali.')
  return tt('Niektoré šablóny sa nevygenerovali - každá beží samostatne, takže ostatné prešli.')
}
